import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Database, Loader2, FileDown, Archive, Upload } from 'lucide-react';

function downloadJSON(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
}

export default function DataExport() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [fullBusy, setFullBusy] = useState(false);
  // Settings_Insights_Audit_Fix Item 4 — Import panel merged in from the old
  // /app/settings/data-backup page (now redirects here).
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState('upsert');
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/settings/backup/collections');
      setGroups(r.groups || []);
      setTotalRows(r.total_rows || 0);
    } catch (err) {
      toast.error('Failed to load collection list');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      // Full backup format: { exported_at, exported_by, collections: { name: [...] } }
      if (parsed.collections && typeof parsed.collections === 'object' && !Array.isArray(parsed.collections)) {
        const res = await apiPost('/settings/backup/import-full', {
          collections: parsed.collections,
          mode: importMode,
          exported_at: parsed.exported_at || null,
          exported_by: parsed.exported_by || null,
        });
        setImportResult({ ...res, fileName: file.name, isFullBackup: true });
        const errNote = res.total_errors > 0 ? ` · ${res.total_errors} errors` : '';
        toast.success(
          `Full backup: ${res.collections_imported} collections · ${res.total_inserted} inserted · ${res.total_updated} updated · ${res.total_skipped} skipped${errNote}`,
          { duration: 8000 },
        );
        load();
        return;
      }

      // Single-collection format: { collection, data: [...] } or raw array
      let collection = parsed.collection;
      let data = parsed.data || parsed;
      if (!Array.isArray(data)) throw new Error('JSON must contain a "data" array (or be a raw array). For full backups use the file produced by the "Download Full Backup" button.');
      if (!collection) {
        const m = file.name.match(/^([a-z_]+)/i);
        if (m) collection = m[1];
        if (!collection) throw new Error('Could not infer collection name from the filename.');
      }
      const res = await apiPost('/settings/backup/import', { collection, data, mode: importMode });
      setImportResult({ ...res, fileName: file.name });
      const errMsg = res.error_count > 0 ? ` · ${res.error_count} errors` : '';
      toast.success(`${collection}: ${res.inserted} inserted · ${res.updated} updated · ${res.skipped} skipped${errMsg}`, { duration: 6000 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const exportOne = async (collection) => {
    setBusy(collection);
    try {
      const r = await apiGet(`/settings/backup/export/${collection}?limit=50000`);
      downloadJSON(`${collection}-${new Date().toISOString().slice(0, 10)}.json`, r);
      toast.success(`Exported ${r.count} rows from "${collection}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
    setBusy(null);
  };

  const exportFull = async () => {
    if (!window.confirm(`Download a FULL backup of all collections (~${totalRows.toLocaleString()} rows total)? This may take a minute.`)) return;
    setFullBusy(true);
    try {
      const r = await apiGet('/settings/backup/full');
      const fname = `ellora-erp-full-backup-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      downloadJSON(fname, r);
      toast.success(`Full backup downloaded — ${r.total_rows.toLocaleString()} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Full backup failed');
    }
    setFullBusy(false);
  };

  return (
    <div className="p-6 space-y-6" data-testid="data-export-page">
      <button onClick={() => navigate('/app/settings')} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-display font-bold text-slate-500 hover:text-[#3b4872]">
        <ArrowLeft size={14} /> Back to Settings
      </button>

      <PageHeader
        title="Data Export & Import"
        subtitle="Download any module's MongoDB collection as JSON, take a full off-site backup, or import data from another environment."
        icon={Database}
      />

      {/* Import panel — merged in from the old Data & Backup page */}
      <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="import-panel">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-sm bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><Upload size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base mb-0.5">Import from JSON</div>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">Upload a JSON file exported from another environment (e.g. preview → production). Supports both a <b>full backup</b> <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">{`{ exported_at, collections: {...} }`}</code> (from the Download Full Backup button below) and a single-collection file <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">{`{ collection, data: [...] }`}</code> from the per-collection JSON buttons below.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-xs uppercase tracking-wider font-display font-bold text-slate-500">Mode</label>
              <select value={importMode} onChange={(e) => setImportMode(e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white" data-testid="import-mode">
                <option value="upsert">Upsert (merge by natural key)</option>
                <option value="skip-existing">Skip existing rows</option>
                <option value="wipe-and-load">Wipe & Load (DANGEROUS · super-admin only)</option>
              </select>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={handleImportFile}
                disabled={importing}
                className="hidden"
                data-testid="import-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50 font-display font-bold uppercase tracking-wider"
                data-testid="import-trigger-btn"
              >
                {importing ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                {importing ? 'Importing…' : 'Choose JSON to Import'}
              </button>
            </div>
            {importResult && (
              <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-sm p-3 space-y-2" data-testid="import-result">
                {importResult.isFullBackup ? (
                  <>
                    <div><b>{importResult.fileName}</b> — Full backup: <b>{importResult.collections_imported}</b> collections · <b>{importResult.total_inserted}</b> inserted · <b>{importResult.total_updated}</b> updated · {importResult.total_skipped} skipped{importResult.total_errors ? <> · <b className="text-red-700">{importResult.total_errors} errors</b></> : null}</div>
                    <details className="text-slate-700">
                      <summary className="cursor-pointer font-semibold">Per-collection breakdown</summary>
                      <ul className="mt-1 space-y-0.5 font-mono text-[10px] max-h-48 overflow-y-auto">
                        {Object.entries(importResult.by_collection || {}).map(([col, r]) => (
                          <li key={col} className="border-t border-emerald-200 pt-0.5">
                            {r.skipped_collection
                              ? <span className="text-amber-700">{col}: skipped ({r.reason})</span>
                              : <span>{col}: {r.inserted}↑ {r.updated}~ {r.skipped}s {r.error_count ? <b className="text-red-600">{r.error_count}err</b> : null}</span>
                            }
                          </li>
                        ))}
                      </ul>
                    </details>
                  </>
                ) : (
                  <>
                    <div><b>{importResult.fileName}</b> → <b>{importResult.collection}</b>: {importResult.rows_received} rows received · <b>{importResult.inserted}</b> inserted · <b>{importResult.updated}</b> updated · {importResult.skipped} skipped{importResult.error_count ? <> · <b className="text-red-700">{importResult.error_count} errors</b></> : null}</div>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <details className="text-red-800 bg-red-50 border border-red-200 rounded-sm p-2">
                        <summary className="cursor-pointer font-semibold">{importResult.error_count} import errors — click to expand</summary>
                        <ul className="mt-2 space-y-1 font-mono text-[10px]">
                          {importResult.errors.map((er, i) => (
                            <li key={i} className="border-t border-red-200 pt-1">
                              <div>{Object.entries(er.row_keys || {}).map(([k, v]) => `${k}=${v}`).join(' · ')}</div>
                              <div className="text-red-700">{er.error}</div>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-sm p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display font-bold text-base text-[#0F172A]">Full Backup</div>
          <div className="text-xs text-slate-600 mt-0.5">
            One JSON file with every collection — {loading ? '…' : <b>{totalRows.toLocaleString()} rows total</b>}. Recommended for off-site backups.
          </div>
        </div>
        <button
          onClick={exportFull}
          disabled={fullBusy || loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3b4872] hover:bg-[#2c365a] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
          data-testid="export-full-btn"
        >
          {fullBusy ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />} {fullBusy ? 'Bundling…' : 'Download Full Backup'}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading collections…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {groups.map(g => (
            <div key={g.group} className="bg-white border border-slate-200 rounded-sm" data-testid={`group-${g.group.replace(/\s/g, '-').toLowerCase()}`}>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-display font-bold text-sm uppercase tracking-[0.12em] text-[#3b4872]">{g.group}</h3>
                <span className="text-xs text-slate-500">{g.total.toLocaleString()} rows</span>
              </div>
              <div className="divide-y divide-slate-100">
                {g.items.map(item => (
                  <div key={item.collection} className="px-5 py-2 flex items-center justify-between hover:bg-slate-50" data-testid={`row-${item.collection}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-[#0F172A] truncate">{item.collection}</div>
                      <div className="text-[10px] text-slate-500">{item.count.toLocaleString()} rows</div>
                    </div>
                    <button
                      onClick={() => exportOne(item.collection)}
                      disabled={busy === item.collection || item.count === 0}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-display font-bold px-2.5 py-1 border border-slate-200 hover:border-[#3b4872] hover:text-[#3b4872] rounded-sm disabled:opacity-40"
                      title={item.count === 0 ? 'Empty — nothing to export' : 'Download as JSON'}
                      data-testid={`export-${item.collection}`}
                    >
                      {busy === item.collection ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
                      {busy === item.collection ? 'Exporting…' : 'JSON'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-slate-500 border-t border-slate-200 pt-4">
        <p><b>How to use these files</b></p>
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li>Each JSON file is a MongoDB-compatible dump and can be re-imported using the <b>Choose JSON to Import</b> button above (admin only).</li>
          <li>To import locally instead: use <code className="bg-slate-100 px-1 rounded-sm">mongoimport --jsonArray --collection &lt;name&gt; --file data.json</code> after extracting the <code className="bg-slate-100 px-1 rounded-sm">data</code> array from the JSON.</li>
          <li>Full backups include every module — keep them encrypted and off-site for compliance.</li>
        </ul>
      </div>
    </div>
  );
}
